import { t } from "../i18n/runtime";

export type DetailParent = "/queue" | "/tasks" | "/results" | "/reports";

const SAFE_RETURN_TO =
  /^\/(?:queue|reports|projects\/[^/?#]+\/(?:reports(?:\/[^/?#]+(?:\/(?:failures|logs))?)?|queue|(?:runs|tasks|results)(?:\/(?:logs|[^/?#]+(?:\/(?:failures|logs))?))?|evaluation-profiles|prompts|targets|new-run|settings(?:\/(?:env|eval|prompts|sync(?:\/candidates\/[^/?#]+)?))?|questions|suites|content(?:\/questions|\/suites\/[^/?#]+)?)|logs(?:\/[^/?#]+)?|runs\/[^/?#]+(?:\/(?:failures|logs))?)(?:\?[^#]*)?$/;

export function projectSuiteDetailPath(projectId: string, suiteId: string): string {
  return `/projects/${encodeURIComponent(projectId)}/content/suites/${encodeURIComponent(suiteId)}`;
}

export function projectRunDetailPath(projectId: string, runId: string, parent: DetailParent): string {
  const section = parent === "/reports" ? "reports" : parent === "/results" ? "results" : "tasks";
  return `/projects/${encodeURIComponent(projectId)}/${section}/${encodeURIComponent(runId)}`;
}

export function projectRunFailuresPath(projectId: string, runId: string, parent: DetailParent): string {
  return `${projectRunDetailPath(projectId, runId, parent)}/failures`;
}

export function projectRunLogsPath(projectId: string, runId: string, parent: DetailParent): string {
  return `${projectRunDetailPath(projectId, runId, parent)}/logs`;
}

export function detailParent(state: unknown, search = ""): DetailParent {
  const stateValue = state && typeof state === "object"
    ? String((state as { from?: unknown }).from ?? "")
    : "";
  const queryValue = new URLSearchParams(search).get("from") ?? "";
  const candidate = queryValue || stateValue;
  if (candidate === "/queue" || candidate === "queue") return "/queue";
  if (candidate === "/tasks" || candidate === "tasks") return "/tasks";
  if (candidate === "/results" || candidate === "results") return "/results";
  return "/reports";
}

export function detailSource(parent: DetailParent): "queue" | "tasks" | "results" | "reports" {
  return parent.slice(1) as "queue" | "tasks" | "results" | "reports";
}

export function withDetailParent(path: string, parent: DetailParent): string {
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}from=${detailSource(parent)}`;
}

export function detailReturnTo(search: string, fallback: string, projectId = ""): string {
  const value = new URLSearchParams(search).get("returnTo") ?? "";
  if (!SAFE_RETURN_TO.test(value)) return fallback;
  if (!projectId) return value;
  const projectPrefix = `/projects/${encodeURIComponent(projectId)}/`;
  return value.startsWith(projectPrefix) ? value : fallback;
}

export function withDetailReturn(path: string, parent: DetailParent, returnTo: string): string {
  const withParent = withDetailParent(path, parent);
  return `${withParent}&returnTo=${encodeURIComponent(returnTo)}`;
}

/** Map a detail returnTo path onto a top-level sidebar destination. */
export function navItemForReturnTo(returnTo: string, fallback: DetailParent = "/reports"): string {
  if (returnTo.startsWith("/logs")) return "/logs";
  if (returnTo.startsWith("/projects/")) return "/projects";
  if (returnTo.startsWith("/queue")) return "/queue";
  if (returnTo.startsWith("/reports")) return "/reports";
  return fallback;
}

export function detailParentLabel(returnTo: string, parent: DetailParent): string {
  if (returnTo.startsWith("/logs")) return t("nav.logs");
  if (/\/projects\/[^/]+\/(?:queue|runs|tasks)(?:\?|$)/.test(returnTo) || returnTo.startsWith("/queue")) return t("route.parent.tasks");
  if (/\/projects\/[^/]+\/results(?:\?|$)/.test(returnTo) || parent === "/results") return t("route.parent.results");
  if (/\/projects\/[^/]+\/reports(?:\?|$)/.test(returnTo) || parent === "/reports" || returnTo.startsWith("/reports")) {
    return t("route.parent.reports");
  }
  return parent === "/queue" ? t("route.parent.queue") : parent === "/tasks" ? t("route.parent.tasks") : parent === "/results" ? t("route.parent.results") : t("route.parent.reports");
}
